import { Button, IconButton } from '@creditornot/cb-button';
import { Cross, CrossCircleFilled, MapDirections } from '@creditornot/cb-icons';
import { colors } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { Tag } from '@creditornot/cb-tag';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { useQueryParam } from 'use-query-params';

import { deleteMenu } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { ConfirmModal } from 'common/components';
import { Typography } from 'common/typography';
import { deleteSharedMenu, getVenueBootstrapStatus } from 'modules/shared-menu/api';
import { buildGetSelectionAllQueryKey } from 'modules/shared-menu/hooks/useSelectionData';
import { VenueBootstrapStatusResponse, VenueLinksResponse } from 'modules/shared-menu/types';
import { useVenueContext } from 'modules/venue/VenueContext';

type MenuDevToolsProps = {
  setForceClassicMenuRender: (value: boolean) => void;
  isRenderingVlma: boolean;
  venueLink: VenueLinksResponse['venue_links'][number] | null | undefined;
};

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background-color: ${colors.wolt4};
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const ErrorContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const THREE_SECONDS_IN_MS = 3000;

export const MenuDevTools = ({ setForceClassicMenuRender, isRenderingVlma, venueLink }: MenuDevToolsProps) => {
  const { selection_id: selectionId = '', selection_is_shared } = venueLink || {};

  const isVlma = !!selectionId;
  const isSingleVenue = isVlma && !selection_is_shared;

  const [devToolsParam, setDevToolsParam] = useQueryParam('devTools');
  const [lowercaseDevtoolsParam, setLowercaseDevtoolsParam] = useQueryParam('devtools');

  const hasDevToolsParam = typeof devToolsParam === 'string' || typeof lowercaseDevtoolsParam === 'string';
  const hasDevToolsSetOnLocalStorage = localStorage.getItem('menuEditorDevTools') === 'true';

  const [previousStatus, setPreviousStatus] = useState<VenueBootstrapStatusResponse['status'] | undefined>();
  const [isDevToolsHidden, setIsDevToolsHidden] = useState(!hasDevToolsParam && !hasDevToolsSetOnLocalStorage);
  const [isDeleteSelectionModalOpen, setIsDeleteSelectionModalOpen] = useState(false);
  const [isDeleteM2MenuModalOpen, setIsDeleteM2MenuModalOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const queryClient = useQueryClient();
  const venue = useVenueContext();

  const { data } = useQuery(['bootstrap-status', venue.id], () => getVenueBootstrapStatus(venue.id), {
    enabled: !isDevToolsHidden,
    refetchInterval: data => (data?.status === 'running' ? THREE_SECONDS_IN_MS : false),
  });

  /**
   * If it's polling, refresh the page to render VLMA after successfully running the bootstrap
   */
  useEffect(() => {
    if (!data?.status) {
      return;
    }

    const changedFromRunningToSuccess = !!previousStatus && previousStatus === 'running' && data?.status === 'success';

    if (!changedFromRunningToSuccess) {
      setPreviousStatus(data?.status);
      return;
    }

    /**
     * hide the toolbar to avoid the tag briefly showing as "This venue has no selection linked to it" before reloading and showing the correct tag
     */
    setIsDevToolsHidden(true);

    window.location.reload();
  }, [data?.status, previousStatus]);

  /**
   * Sets the value in localStorage if the param is present in the URL and it's not yet set in localStorage
   */
  useEffect(() => {
    if (!hasDevToolsParam && !hasDevToolsSetOnLocalStorage) {
      return;
    }

    localStorage.setItem('menuEditorDevTools', 'true');
  }, [hasDevToolsParam, hasDevToolsSetOnLocalStorage]);

  const { mutateAsync: executeDeleteSelection, isLoading: isDeletingSelection } = useMutation(() =>
    deleteSharedMenu(selectionId),
  );

  const { mutateAsync: executeDeleteM2Menu, isLoading: isDeletingM2Menu } = useMutation(
    () => {
      if (!venue.menu) {
        return Promise.reject();
      }

      return deleteMenu(venue.menu);
    },
    {
      onSuccess: () => {
        toast.success('m2 menu deleted!');
        window.location.reload();
      },
    },
  );

  const hideToolbar = useCallback(() => {
    setIsDevToolsHidden(true);
    setDevToolsParam(undefined);
    setLowercaseDevtoolsParam(undefined);
    localStorage.removeItem('menuEditorDevTools');
  }, [setDevToolsParam, setLowercaseDevtoolsParam]);

  const rollbackToCme = useCallback(async () => {
    try {
      await executeDeleteSelection();

      setIsDeleteSelectionModalOpen(false);
      setForceClassicMenuRender(true);

      // invalidate VLMA-related queries
      queryClient.invalidateQueries(['shared-menu-link', venue.id]);
      const selectionAllQueryKey = buildGetSelectionAllQueryKey(selectionId, false);
      queryClient.invalidateQueries(selectionAllQueryKey);

      toast.success('Successfully unlinked and deleted selection');
    } catch (error) {
      toast.error(error?.message || 'Something went wrong', { duration: Infinity });
    }
  }, [executeDeleteSelection, queryClient, selectionId, setForceClassicMenuRender, venue.id]);

  if (isDevToolsHidden) {
    return null;
  }

  return (
    <>
      <Root>
        <TagsContainer>
          <Tag size="small" variant="lightBlue">
            Venue ID: {venue.id}
          </Tag>
          {!selectionId && data?.status !== 'running' && (
            <Tag size="small" variant="lightOrange">
              This venue has no selection linked to it
            </Tag>
          )}
          {selectionId && (
            <Tag size="small" variant="lightBlue">
              {isSingleVenue ? 'Single Venue' : 'Shared Venue'}
            </Tag>
          )}
          {data?.status === 'success' && isSingleVenue && (
            <Tag size="small" variant="lightGreen">
              Bootstrap success
            </Tag>
          )}
          {data?.status === 'failure' && isSingleVenue && (
            <LegacyPopover
              show={isPopoverOpen}
              placement="bottom"
              content={<Typography.Small>{data?.error_message}</Typography.Small>}
              style={{ maxWidth: '500px' }}
            >
              <ErrorContainer onMouseEnter={() => setIsPopoverOpen(true)} onMouseLeave={() => setIsPopoverOpen(false)}>
                <Tag size="small" variant="lightRed">
                  Bootstrap error
                </Tag>
                <Typography.Small>{data?.error_message.substring(0, 30)} (...)</Typography.Small>
              </ErrorContainer>
            </LegacyPopover>
          )}
          {data?.status === 'pending' && isSingleVenue && (
            <Tag size="small" variant="lightBlack">
              Bootstrap pending
            </Tag>
          )}
          {data?.status === 'running' && (
            <Tag size="small" variant="outlineBlue" icon={<Spinner size="tiny" />}>
              Bootstrap running
            </Tag>
          )}
        </TagsContainer>
        <ButtonsContainer>
          {!isRenderingVlma && venue.menu && (
            <Button
              size="tiny"
              icon={<CrossCircleFilled />}
              variant="lightRed"
              onClick={() => setIsDeleteM2MenuModalOpen(true)}
            >
              Delete m2 menu
            </Button>
          )}
          {isRenderingVlma ? (
            <>
              {isSingleVenue && (
                <Button
                  size="tiny"
                  icon={<CrossCircleFilled />}
                  variant="lightRed"
                  onClick={() => setIsDeleteSelectionModalOpen(true)}
                >
                  Unlink & Delete selection
                </Button>
              )}

              <Button
                size="tiny"
                icon={<MapDirections />}
                variant="lightBlue"
                onClick={() => setForceClassicMenuRender(true)}
              >
                Switch to CME
              </Button>
            </>
          ) : (
            <>
              {selectionId && (
                <Button
                  size="tiny"
                  icon={<MapDirections />}
                  variant="lightBlue"
                  onClick={() => setForceClassicMenuRender(false)}
                >
                  Switch to VLMA
                </Button>
              )}
            </>
          )}
          <IconButton size="tiny" variant="lightGrey" onClick={hideToolbar}>
            <Cross />
          </IconButton>
        </ButtonsContainer>
      </Root>

      <ConfirmModal
        header="Are you sure?"
        description={
          <Typography.Body3>
            This will execute the following steps
            <ol>
              <li>Unlink this venue from this selection</li>
              <li>Delete the selection from the Offering Platform</li>
            </ol>
            As a result this venue will revert back to the CME
          </Typography.Body3>
        }
        onCancel={() => setIsDeleteSelectionModalOpen(false)}
        onConfirm={rollbackToCme}
        open={isDeleteSelectionModalOpen}
        loading={isDeletingSelection}
      />

      <ConfirmModal
        header="Delete m2 menu"
        description={
          <Typography.Body3>This will delete the m2 menu. Only use if you know what you're doing.</Typography.Body3>
        }
        onCancel={() => setIsDeleteM2MenuModalOpen(false)}
        onConfirm={executeDeleteM2Menu}
        open={isDeleteM2MenuModalOpen}
        loading={isDeletingM2Menu}
      />
    </>
  );
};
